import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Mail, Phone, MapPin, Send, CheckCircle2,
  Award, RefreshCw, Shield, TrendingUp, Cpu, CheckCircle,
  Clock, MessageSquare, Rocket,
} from "lucide-react";
import PortfolioNavbar from "./PortfolioNavbar";
import PortfolioFooter from "./PortfolioFooter";

const advantages = [
  { icon: <Award className="w-5 h-5 text-orange-500" />,     title: "Engineering Excellence",   detail: "Senior engineers with 5+ years of SaaS and enterprise experience on every project." },
  { icon: <RefreshCw className="w-5 h-5 text-orange-500" />, title: "Agile & Transparent SDLC", detail: "Weekly demos, sprint reports, and full visibility into every stage of delivery." },
  { icon: <Shield className="w-5 h-5 text-orange-500" />,    title: "Security First",           detail: "Zero-trust architecture, regular audits, and NDA-protected collaboration." },
  { icon: <TrendingUp className="w-5 h-5 text-orange-500" />,title: "Scalable Outcomes",        detail: "Systems architected to grow with your business — from MVP to millions of users." },
  { icon: <Cpu className="w-5 h-5 text-orange-500" />,       title: "Flexible Team Scaling",    detail: "Scale your dedicated engineering team up or down based on project phase." },
  { icon: <CheckCircle className="w-5 h-5 text-orange-500" />,title: "Vetted Talent",           detail: "Immediate access to thoroughly vetted full-stack, mobile, and cloud engineers." },
];

const nextSteps = [
  { icon: <MessageSquare className="w-6 h-6 text-orange-400" />, step: "01", title: "We Review Your Brief",  desc: "Our team reads your message and assigns the right specialist within hours." },
  { icon: <Clock className="w-6 h-6 text-orange-400" />,         step: "02", title: "Discovery Call",        desc: "A 30-minute call to understand your goals, timeline, and budget." },
  { icon: <Rocket className="w-6 h-6 text-orange-400" />,        step: "03", title: "Proposal in 48 Hours",  desc: "You receive a scoped proposal with approach, timeline, and pricing." },
];

const InputField = ({ label, id, type = "text", value, onChange, required, placeholder }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-bold text-gray-700 mb-1.5">
      {label} {required && <span className="text-orange-500">*</span>}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="w-full px-4 py-3 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 placeholder-gray-400"
    />
  </div>
);

const ContactUs = () => {
  const [form, setForm] = React.useState({ name: "", email: "", company: "", service: "", message: "" });
  const [submitted, setSubmitted] = React.useState(false);
  const [sending, setSending] = React.useState(false);

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => { setSending(false); setSubmitted(true); }, 1200);
  };

  return (
    <div className="font-sans bg-white">
      <Helmet>
        <title>Contact Us | Winsil Evolution Pvt Ltd</title>
        <meta name="description" content="Get in touch with Winsil Evolution for software development, branding, and digital marketing services. We respond within 24 hours." />
      </Helmet>

      <PortfolioNavbar />

      {/* ── Hero ── */}
      <div className="relative bg-gray-950 pt-32 pb-20 px-4 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-orange-400" />
        <div className="relative z-10 max-w-3xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-400 mb-4">Let's Talk</p>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-5 leading-tight">
            Start a Conversation
          </h1>
          <p className="text-white/50 text-lg leading-relaxed">
            Whether it's a software build, a rebrand, or just an idea — drop us a message and we'll respond within one business day.
          </p>
        </div>
      </div>

      {/* ── Main Grid: Form + Sidebar ── */}
      <div className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-10">

          {/* Form */}
          <div className="lg:col-span-3 bg-white rounded-2xl shadow-xl p-8 border-t-4 border-orange-500">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-20 h-20 rounded-full bg-green-50 border-2 border-green-200 flex items-center justify-center mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="text-2xl font-extrabold text-gray-900 mb-3">Message Sent!</h3>
                <p className="text-gray-500 max-w-sm text-sm leading-relaxed">
                  Thanks for reaching out. A member of our team will be in touch within one business day.
                </p>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-extrabold text-gray-900 mb-1">Send Us a Message</h2>
                <p className="text-sm text-gray-500 mb-7">We read every enquiry and respond personally.</p>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <InputField label="Full Name"   id="name"    value={form.name}    onChange={handleChange("name")}    required placeholder="Rahul Sharma" />
                    <InputField label="Work Email"  id="email"   type="email" value={form.email}   onChange={handleChange("email")}   required placeholder="rahul@company.com" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <InputField label="Company"     id="company" value={form.company} onChange={handleChange("company")} placeholder="Company name (optional)" />
                    <div>
                      <label htmlFor="service" className="block text-sm font-bold text-gray-700 mb-1.5">
                        Service Needed <span className="text-orange-500">*</span>
                      </label>
                      <select
                        id="service"
                        value={form.service}
                        onChange={handleChange("service")}
                        required
                        className="w-full px-4 py-3 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                      >
                        <option value="">Select a service…</option>
                        <optgroup label="Software">
                          <option value="software-development">Software Development</option>
                          <option value="core-solutions">Core Solutions / Web & Mobile</option>
                          <option value="strategy-architecture">Strategy & Architecture</option>
                          <option value="tech-stack">Tech Stack & Tooling</option>
                        </optgroup>
                        <optgroup label="Branding">
                          <option value="social-media">Social Media Marketing</option>
                          <option value="digital-marketing">Digital Marketing</option>
                          <option value="seo-content">SEO & Content</option>
                          <option value="uiux">UI/UX & Brand Identity</option>
                        </optgroup>
                        <option value="other">Other / Not Sure Yet</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-bold text-gray-700 mb-1.5">
                      Message <span className="text-orange-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      rows={5}
                      value={form.message}
                      onChange={handleChange("message")}
                      required
                      placeholder="Tell us about your project, goals, and timeline..."
                      className="w-full px-4 py-3 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 placeholder-gray-400 resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full flex items-center justify-center gap-2.5 py-4 px-6 text-sm font-black uppercase tracking-wider rounded-xl bg-orange-500 text-white hover:bg-orange-400 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 shadow-lg shadow-orange-500/25 hover:scale-[1.02]"
                  >
                    {sending ? (
                      <>
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                        </svg>
                        Sending…
                      </>
                    ) : (
                      <><Send className="w-4 h-4" /> Send Message</>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-2 space-y-5">
            {/* Contact cards */}
            {[
              { icon: <MapPin className="w-5 h-5 text-orange-400 flex-shrink-0" />, label: "Office", value: "New Delhi, India — 110001" },
              { icon: <Mail className="w-5 h-5 text-orange-400 flex-shrink-0" />,   label: "Email",  value: "winsilevolution@gmail.com", href: "mailto:winsilevolution@gmail.com" },
              { icon: <Phone className="w-5 h-5 text-orange-400 flex-shrink-0" />,  label: "Phone",  value: "+91 9560116320", href: "tel:+919560116320" },
            ].map((item) => (
              <div key={item.label} className="flex items-start space-x-4 p-5 bg-white rounded-xl shadow-md border border-gray-100">
                <div className="w-10 h-10 bg-orange-50 border border-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-gray-400 mb-1">{item.label}</p>
                  {item.href ? (
                    <a href={item.href} className="text-sm font-bold text-gray-800 hover:text-orange-500 transition-colors">{item.value}</a>
                  ) : (
                    <p className="text-sm font-bold text-gray-800">{item.value}</p>
                  )}
                </div>
              </div>
            ))}

            {/* Response time badge */}
            <div className="p-6 bg-gray-950 rounded-xl text-white">
              <p className="text-xs font-black uppercase tracking-widest text-orange-400 mb-2">Typical Response</p>
              <p className="text-4xl font-black mb-1">&lt; 24 hrs</p>
              <p className="text-xs text-white/40">Business days. For urgent matters, call us directly.</p>
            </div>

            {/* Image */}
            <div className="relative rounded-xl overflow-hidden h-48 shadow-md">
              <img src="/images/photo-1573164713988-8665fc963095.jpg" alt="Winsil team at work" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="text-white font-black text-sm uppercase tracking-wider">Our Team</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── What Happens Next ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-500 text-center mb-2">Process</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-14">What Happens After You Submit</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {nextSteps.map((s) => (
              <div key={s.step} className="relative bg-gray-50 border border-gray-100 rounded-2xl p-8 text-center hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <p className="text-6xl font-black text-gray-100 absolute top-6 right-6 leading-none">{s.step}</p>
                <div className="w-14 h-14 bg-gray-900 rounded-xl flex items-center justify-center mx-auto mb-5 relative z-10">
                  {s.icon}
                </div>
                <h3 className="text-lg font-extrabold text-gray-900 mb-3 relative z-10">{s.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed relative z-10">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Why Work With Us ── */}
      <div className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-950">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-orange-400 text-center mb-2">Why Winsil</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white text-center mb-14">Your Technical & Creative Advantage</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advantages.map((item, i) => (
              <div key={i} className="flex items-start space-x-4 p-6 bg-gray-900 border border-gray-800 rounded-2xl hover:border-orange-500/40 transition-all duration-300">
                <div className="w-10 h-10 bg-orange-500/10 border border-orange-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="text-sm font-extrabold text-white mb-1">{item.title}</h4>
                  <p className="text-xs text-gray-400 leading-relaxed">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Bottom image strip ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 h-56">
        {[
          { src: "/images/photo-1522202176988-66273c2fd55f.jpg", label: "Collaboration" },
          { src: "/images/photo-1516321318423-f06f85e504b3.jpg", label: "Delivery" },
          { src: "/images/photo-1461749280684-dab374f5cde3.jpg", label: "Engineering" },
        ].map((img) => (
          <div key={img.label} className="relative overflow-hidden group">
            <img src={img.src} alt={img.label} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gray-900/50" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-white font-black text-sm uppercase tracking-widest">{img.label}</span>
            </div>
          </div>
        ))}
      </div>

      <PortfolioFooter />
    </div>
  );
};

export default ContactUs;
